import React, { useState, useEffect } from "react";
import {
  ConstrainedDialog as Dialog,
  ConstrainedDialogContent as DialogContent,
  ConstrainedDialogDescription as DialogDescription,
  ConstrainedDialogFooter as DialogFooter,
  ConstrainedDialogHeader as DialogHeader,
  ConstrainedDialogTitle as DialogTitle,
} from "@/components/ui/constrained-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DateTimePicker } from "@/components/ui/date-time-picker";
import { Interview } from "@/services/interviewService";
import AutocompleteInput from "@/components/ui/autocomplete-input";
import {
  getExpertNetworks,
  getExistingAngles,
} from "@/services/autocompleteService";
import { aiService } from "@/services/serviceFactory";
import { toast } from "@/hooks/use-toast";
import { useTheme } from "@/contexts/ThemeContext";
import { track } from "@vercel/analytics";

interface AddInterviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (interviewData: Partial<Interview>) => Promise<void>;
  projectId?: string; // ID of the current project to scope autocomplete data
}

const AddInterviewModal: React.FC<AddInterviewModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  projectId,
}) => {
  const { effectiveTheme } = useTheme();
  const [expertFirstName, setExpertFirstName] = useState("");
  const [expertLastName, setExpertLastName] = useState("");
  const [company, setCompany] = useState("");
  const [title, setTitle] = useState("");
  const [network, setNetwork] = useState("");
  const [date, setDate] = useState<Date | undefined>(undefined);
  const [duration, setDuration] = useState("60");
  const [price, setPrice] = useState("0");
  const [status, setStatus] = useState<
    | "scheduled"
    | "completed"
    | "canceled"
    | "cancelled w/ fee"
    | "in progress"
    | "follow-up"
    | "follow-up scheduled"
    | "follow-up completed"
  >("scheduled");
  const [angle, setAngle] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // AI parsing states
  const [calendarText, setCalendarText] = useState("");
  const [isParsingAI, setIsParsingAI] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);

  // Autocomplete data
  const [expertNetworks, setExpertNetworks] = useState<string[]>([]);
  const [existingAngles, setExistingAngles] = useState<string[]>([]);

  // Load autocomplete data on mount
  useEffect(() => {
    const loadAutocompleteData = async () => {
      try {
        const [networks, angles] = await Promise.all([
          getExpertNetworks(),
          getExistingAngles(projectId),
        ]);
        setExpertNetworks(networks);
        setExistingAngles(angles);
      } catch (error) {
        console.error("Error loading autocomplete data:", error);
      }
    };

    if (isOpen) {
      loadAutocompleteData();
    }
  }, [isOpen, projectId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const interviewData: Partial<Interview> = {
      expertFirstName,
      expertLastName,
      company,
      title,
      network,
      date,
      duration,
      initialInterviewCost: price,
      status,
      angle,
      // anonymizedAttribution and interviewDescription will be auto-generated
    };

    try {
      setIsSubmitting(true);
      await onSubmit(interviewData);

      // Track interview addition
      track("interview_added", {
        network: network || "none",
        status: status,
        has_angle: !!angle,
        project_id: projectId || "unknown",
        added_via: calendarText ? "ai_parse" : "manual",
      });

      resetForm();
      onClose();
    } catch (error) {
      console.error("Error submitting interview:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setExpertFirstName("");
    setExpertLastName("");
    setCompany("");
    setTitle("");
    setNetwork("");
    setDate(undefined);
    setDuration("60");
    setPrice("0");
    setStatus("scheduled");
    setAngle("");
    setCalendarText("");
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleAIParseCalendar = async () => {
    if (!calendarText.trim()) {
      toast({
        title: "Error",
        description: "Please paste some calendar text to parse.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsParsingAI(true);
      const parsedData = await aiService.parseCalendarText(
        calendarText,
        projectId
      );

      // Fill in the form with the parsed data
      setExpertFirstName(parsedData.expertFirstName || "");
      setExpertLastName(parsedData.expertLastName || "");
      setCompany(parsedData.company || "");
      setTitle(parsedData.title || "");
      setNetwork(parsedData.network || "");

      // Parse date string into Date object for DateTimePicker
      if (parsedData.date) {
        try {
          const dateObj = new Date(parsedData.date);
          setDate(dateObj);
        } catch (error) {
          console.error("Error parsing date:", error);
          setDate(undefined);
        }
      } else {
        setDate(undefined);
      }

      setDuration(parsedData.duration || "60");
      setPrice(parsedData.initialInterviewCost || "0"); // Use auto-calculated price
      setAngle(parsedData.angle || "");

      toast({
        title: "Success",
        description:
          "Calendar text parsed successfully! Review and edit the fields above as needed.",
      });
    } catch (error) {
      console.error("Error parsing calendar text:", error);
      toast({
        title: "Error",
        description:
          "Failed to parse calendar text. Please try again or enter manually.",
        variant: "destructive",
      });
    } finally {
      setIsParsingAI(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add New Interview</DialogTitle>
          <DialogDescription>
            Paste your calendar invite text below to automatically extract
            interview details, or fill in the fields manually.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          {/* Reduced padding & gap to make modal more compact */}
          <div className="grid gap-3 py-1">
            {/* AI Parser Section - Primary Method */}
            <div className="space-y-2">
              <div>
                <div className="flex items-center gap-1 mb-1">
                  <Label htmlFor="calendarText" className="text-sm font-medium">
                    AI Calendar Parser (Recommended)
                  </Label>
                  <div className="relative">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-5 w-5 p-0 text-muted-foreground hover:text-foreground flex items-center justify-center"
                      onClick={() => setShowTutorial(!showTutorial)}
                      type="button"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="h-4 w-4"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 5.25h.008v.008H12v-.008Z"
                        />
                      </svg>
                    </Button>
                    {showTutorial && (
                      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                        <div className="relative w-full max-w-[580px] mx-4 p-4 bg-popover border rounded-md shadow-lg">
                          <button
                            onClick={() => setShowTutorial(false)}
                            className="absolute top-2 right-2 h-6 w-6 flex items-center justify-center rounded-full bg-background border hover:bg-accent"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth={1.5}
                              stroke="currentColor"
                              className="h-4 w-4"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M6 18 18 6M6 6l12 12"
                              />
                            </svg>
                          </button>
                          <div className="mb-2">
                            <h3 className="text-lg font-semibold">
                              How to Copy Calendar Invite
                            </h3>
                            <p className="text-sm text-muted-foreground">
                              Follow this tutorial to copy all the details from
                              your calendar invite
                            </p>
                          </div>
                          <img
                            src="/copy_invite_tutorial.gif"
                            alt="How to copy calendar invite tutorial"
                            className="w-full rounded"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mb-1">
                  Paste calendar invite text below to auto-fill the fields.
                </p>
                <Textarea
                  id="calendarText"
                  value={calendarText}
                  onChange={(e) => setCalendarText(e.target.value)}
                  placeholder="Paste your raw calendar invite text here... ctrl+a, ctrl+c inside the calendar invite to copy ALL invite details."
                  rows={4}
                  className="min-h-[100px] focus-visible:ring-0 focus-visible:ring-offset-0"
                />
              </div>
              <Button
                type="button"
                onClick={handleAIParseCalendar}
                disabled={!calendarText.trim() || isParsingAI}
                variant={calendarText.trim() ? "default" : "outline"}
                className="w-full"
              >
                {isParsingAI ? "Parsing..." : "Parse Calendar & Fill Fields"}
              </Button>
            </div>

            {/* Manual Input Fields - Secondary Method */}
            <div className="border-t pt-1">
              <div className="space-y-3">
                {/* Expert Name Row */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="expertFirstName">First Name *</Label>
                    <Input
                      id="expertFirstName"
                      value={expertFirstName}
                      onChange={(e) => setExpertFirstName(e.target.value)}
                      required
                      className="focus-visible:ring-0 focus-visible:ring-offset-0"
                    />
                  </div>
                  <div>
                    <Label htmlFor="expertLastName">Last Name *</Label>
                    <Input
                      id="expertLastName"
                      value={expertLastName}
                      onChange={(e) => setExpertLastName(e.target.value)}
                      required
                      className="focus-visible:ring-0 focus-visible:ring-offset-0"
                    />
                  </div>
                </div>

                {/* Company and Title Row */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="company">Company</Label>
                    <Input
                      id="company"
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                      className="focus-visible:ring-0 focus-visible:ring-offset-0"
                    />
                  </div>
                  <div>
                    <Label htmlFor="title">Title</Label>
                    <Input
                      id="title"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      className="focus-visible:ring-0 focus-visible:ring-offset-0"
                    />
                  </div>
                </div>

                {/* Date and Duration Row */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="date">Interview Date & Time</Label>
                    <DateTimePicker date={date} setDate={setDate} />
                  </div>
                  <div>
                    <Label htmlFor="duration">Duration (minutes)</Label>
                    <Input
                      id="duration"
                      type="number"
                      value={duration}
                      onChange={(e) => setDuration(e.target.value)}
                      placeholder="60"
                      min="15"
                      step="15"
                      className="focus-visible:ring-0 focus-visible:ring-offset-0"
                      style={{
                        colorScheme:
                          effectiveTheme === "dark" ? "dark" : "auto",
                      }}
                    />
                  </div>
                </div>

                {/* Network and Angle Row */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="network">Expert Network</Label>
                    <AutocompleteInput
                      id="network"
                      value={network}
                      onChange={setNetwork}
                      options={expertNetworks}
                      placeholder="Type to search networks..."
                    />
                  </div>
                  <div>
                    <Label htmlFor="angle">Angle</Label>
                    <AutocompleteInput
                      id="angle"
                      value={angle}
                      onChange={setAngle}
                      options={existingAngles}
                      placeholder="Type to search angles..."
                    />
                  </div>
                </div>

                {/* Price and Status Row */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="price">Interview Cost ($)</Label>
                    <Input
                      id="price"
                      type="number"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                      placeholder="0"
                      min="0"
                      className="focus-visible:ring-0 focus-visible:ring-offset-0"
                      style={{
                        colorScheme:
                          effectiveTheme === "dark" ? "dark" : "auto",
                      }}
                    />
                  </div>
                  <div>
                    <Label htmlFor="status">Status</Label>
                    <select
                      id="status"
                      value={status}
                      onChange={(e) =>
                        setStatus(
                          e.target.value as
                            | "scheduled"
                            | "completed"
                            | "canceled"
                            | "cancelled w/ fee"
                            | "in progress"
                            | "follow-up"
                            | "follow-up scheduled"
                            | "follow-up completed"
                        )
                      }
                      className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm text-foreground shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600"
                    >
                      <option value="scheduled">Scheduled</option>
                      <option value="completed">Completed</option>
                      <option value="canceled">Canceled</option>
                      <option value="cancelled w/ fee">Cancelled w/ fee</option>
                      <option value="in progress">In Progress</option>
                      <option value="follow-up scheduled">
                        Follow-up Scheduled
                      </option>
                      <option value="follow-up completed">
                        Follow-up Completed
                      </option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!expertFirstName || !expertLastName || isSubmitting}
            >
              {isSubmitting ? "Adding..." : "Add Interview"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddInterviewModal;
